import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type WorkObjectFieldValueItem = Awaited<ReturnType<typeof trpc.workObjects.fieldValues.query>>[number];

export type UpdateWorkObjectFieldValuesInput = {
  values: WorkObjectFieldValueItem[];
  workObjectId: string;
};

export function useWorkObjectFieldValues(workObjectId: string | null) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const valuesQuery = useQuery(trpc.workObjects.fieldValues.queryOptions(workObjectId ? { workObjectId } : skipToken));
  const updateMutation = useMutation(trpc.workObjects.updateFieldValues.mutationOptions());
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (workObjectId) await valuesQuery.refetch();
  }, [valuesQuery.refetch, workObjectId]);

  async function updateFieldValues(input: UpdateWorkObjectFieldValuesInput) {
    setError(null);
    try {
      const updated = await updateMutation.mutateAsync(input);
      queryClient.setQueryData(trpc.workObjects.fieldValues.queryKey({ workObjectId: input.workObjectId }), updated);
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save field values.");
      throw err;
    }
  }

  function replaceLocalValues(values: WorkObjectFieldValueItem[]) {
    if (!workObjectId) return;
    queryClient.setQueryData(trpc.workObjects.fieldValues.queryKey({ workObjectId }), values);
  }

  return {
    data: valuesQuery.data ?? [],
    error: error ?? (valuesQuery.error instanceof Error ? valuesQuery.error.message : valuesQuery.error ? String(valuesQuery.error) : null),
    isLoading: valuesQuery.isPending,
    isUpdating: updateMutation.isPending,
    reload,
    replaceLocalValues,
    updateFieldValues,
  };
}
